/**
 * Funciones tipadas de los endpoints /training-plans/* (Sprint 6 Bloque 4).
 *
 * Espejo de backend/app/blueprints/training_plans/routes.py.
 *
 * Patrón consistente con `src/api/strava.ts`:
 * - 404 esperados se traducen a `null` (no_active_plan, out_of_plan_window).
 * - 400 estructurados se propagan como ApiError para que la UI decida.
 */

import { apiFetch } from "./client";
import {
  ApiError,
  type ActivePlanResponse,
  type ActivePlanResult,
  type AddRacePayload,
  type AddRaceResult,
  type CreatePlanPayload,
  type CurrentWeekResponse,
  type MaintenancePlanPayload,
  type MaintenancePlanResponse,
  type PatchSessionPayload,
  type PatchSessionResponse,
  type TrainingPlan,
  type VamResultPayload,
  type VamResultResponse,
} from "@/types/api";

/** Resultado "vacío" de `getActivePlan` — sin plan activo y sin flags.
 *  Usado para 404 legacy y como base. */
const _EMPTY_ACTIVE_PLAN_RESULT: ActivePlanResult = {
  plan: null,
  recommendations: [],
  postRecoveryOptionsAvailable: false,
  progressionDistanceChoicePending: false,
  raceJustFinished: false,
};

/**
 * GET /training-plans/active — plan activo + recomendaciones del adapter.
 *
 * Sprint 8 S8.1 (API-001 + API-001-BIS): el backend ahora devuelve
 * **200 con `{plan: null, recommendations: []}`** cuando no hay plan
 * activo. Aceptamos AMBAS formas durante migración (1-2 sprints):
 *
 * - 200 + `plan === null` → `null` (nueva semántica).
 * - 200 + `plan` presente → `data` tipado.
 * - 404 `no_active_plan` → `null` (legacy; sigue funcionando para mocks
 *   de tests integración existentes que no se han migrado).
 * - Otros 4xx/5xx → `ApiError` propagado.
 *
 * Esto distingue "no hay plan" (mostrar empty state) de "error de red"
 * (propagado como ApiError) sin generar ruido 404 en logs prod.
 */
export async function getActivePlan(): Promise<ActivePlanResult> {
  try {
    // Sprint 13 S13.10a: parseamos el response crudo (snake_case) a
    // `ActivePlanResult` (camelCase, plan nullable). Esto EXPONE los flags
    // `post_recovery_options_available` (S13.4) y
    // `progression_distance_choice_pending` (S13.8) que llegan justamente
    // cuando `plan === null` — el shape antiguo (`TrainingPlan | null`)
    // los descartaba. NUNCA devuelve null: "sin plan" = `{plan: null}`.
    const data = await apiFetch<ActivePlanResponse>("/training-plans/active");
    return {
      plan: data.plan,
      recommendations: data.recommendations ?? [],
      postRecoveryOptionsAvailable: data.post_recovery_options_available ?? false,
      progressionDistanceChoicePending:
        data.progression_distance_choice_pending ?? false,
      // v16.5.X T1 4b sub-paquete X.
      raceJustFinished: data.race_just_finished ?? false,
    };
  } catch (err) {
    if (err instanceof ApiError && err.status === 404 && err.code === "no_active_plan") {
      // 404 legacy (mocks/tests integración no migrados): sin plan.
      return { ..._EMPTY_ACTIVE_PLAN_RESULT };
    }
    throw err;
  }
}

/**
 * GET /training-plans/active/current-week — solo la semana que contiene hoy.
 *
 * `null` si:
 * - 404 no_active_plan: user sin plan.
 * - 404 out_of_plan_window: plan existe pero hoy fuera del rango (carrera
 *   pasada o pre-arranque). El caller debe diferenciar por si necesita
 *   distinto UX, pero aquí ambos casos = "no hay semana actual".
 */
export async function getCurrentWeek(): Promise<CurrentWeekResponse | null> {
  try {
    return await apiFetch<CurrentWeekResponse>("/training-plans/active/current-week");
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      return null;
    }
    throw err;
  }
}

/**
 * POST /training-plans — genera + persiste plan nuevo.
 *
 * Soft-deletea el plan activo previo si existe (decisión backend Bloque 3).
 *
 * Errores típicos:
 * - 400 profile_incomplete: AthleteProfile sin hr_max/vam_pace/surface/days.
 *   El error tiene `missing_fields[]` y `onboarding_url` en el body. El
 *   frontend debe redirigir a /onboarding (cuando exista — Bloque 5).
 * - 400 race_date_in_past_or_today / race_date_too_far / race_date_invalid /
 *   race_distance_required.
 */
export async function createPlan(payload: CreatePlanPayload): Promise<TrainingPlan> {
  const data = await apiFetch<{ plan: TrainingPlan }>("/training-plans", {
    method: "POST",
    body: payload,
  });
  return data.plan;
}

/**
 * Sub-paquete Y v16.5.AB — POST /training-plans con plan_type='maintenance'.
 *
 * Crea un plan de mantenimiento (ciclo 2 sem A/B alternada). El backend:
 * - Hereda `race_distance` del último plan del atleta (3-tier fallback,
 *   ver `_get_inherited_race_distance` Y.B.3).
 * - Sintetiza `race_date = start_date + 2 sem` (modelo lo exige NOT NULL).
 * - Reemplaza (soft-delete + supersede) la recovery week activa si existe,
 *   mismo flujo que `createPlan` con `supersede_previous=True`.
 * - Actualiza tracking del User (`consecutive_maintenance_cycles` +
 *   `last_maintenance_started_at`) en transacción posterior.
 *
 * @param payload - Opcional. Solo `start_date` (mismo rango que progression:
 *   `[today, today+30d]`). Si omitido, backend usa `_next_monday(today)`.
 * @returns `MaintenancePlanResponse` con plan + `cycle_number` (0..5) +
 *   `was_reset` (true si counter reseteado por NULL o >30d sin renovar).
 */
export async function createMaintenancePlan(
  payload?: Partial<Omit<MaintenancePlanPayload, "plan_type">>,
): Promise<MaintenancePlanResponse> {
  return apiFetch<MaintenancePlanResponse>("/training-plans", {
    method: "POST",
    body: {
      plan_type: "maintenance",
      ...(payload ?? {}),
    },
  });
}

/**
 * POST /training-plans/active/add-race — Sprint 13 S13.9/S13.10b (v10.1 §3).
 *
 * Añade una carrera (asfalto o trail) a un Plan de Progresión activo,
 * reorientándolo hacia `race_date`. Devuelve el plan reorientado (201).
 *
 * IMPORTANTE — manejo del 409: si la carrera es de trail y el perfil del
 * atleta es de asfalto, el backend responde **409
 * `trail_modality_confirmation_required`** SIN mutar nada. Esto NO es un
 * error genérico: es una señal esperada. El caller debe capturar el
 * `ApiError` y comprobar `err.code === "trail_modality_confirmation_required"`
 * para mostrar el diálogo de confirmación, y reintentar esta misma llamada
 * con `confirm_trail_modality: true`.
 */
export async function addRaceToActivePlan(
  payload: AddRacePayload,
): Promise<AddRaceResult> {
  return apiFetch<AddRaceResult>("/training-plans/active/add-race", {
    method: "POST",
    body: payload,
  });
}

/**
 * PATCH /training-plans/active/sessions/<id> — skip o complete_manual.
 *
 * El backend devuelve la sesión actualizada + (si skip) la adaptación
 * aplicada al plan futuro (`adaptation` con `next_intensity_to_z2`,
 * `continue_normal`, etc + posible `alert` si 2 sem consecutivas
 * incompletas).
 *
 * 404 si la sesión no pertenece al user (protección cross-user backend).
 */
export async function patchSession(
  sessionId: number,
  payload: PatchSessionPayload,
): Promise<PatchSessionResponse> {
  return apiFetch<PatchSessionResponse>(
    `/training-plans/active/sessions/${sessionId}`,
    {
      method: "PATCH",
      body: payload,
    },
  );
}

/**
 * DELETE /training-plans/active — soft-delete del plan activo.
 *
 * Idempotente: 204 también si no había plan activo (no error).
 */
export async function deleteActivePlan(): Promise<void> {
  return apiFetch<void>("/training-plans/active", { method: "DELETE" });
}

/**
 * POST /training-plans/race-finished-dismiss — cierra el banner race+1.
 *
 * v16.5.X T1 4b sub-paquete X (Alberto 19/06/26). El atleta cierra el
 * banner `PostRaceImmediateBanner`. Backend persiste
 * `users.race_finished_dismissed_at = utcnow()`. El siguiente GET
 * /training-plans/active devuelve `raceJustFinished=false` durante 24h;
 * tras 24h el flag se re-activa (snooze suave) hasta los 30 días desde
 * la transición race→recovery.
 *
 * Idempotente: llamadas repetidas sobreescriben el timestamp.
 */
export async function dismissRaceFinishedMessage(): Promise<void> {
  return apiFetch<void>("/training-plans/race-finished-dismiss", {
    method: "POST",
  });
}

/**
 * POST /training-plans/active/sessions/<id>/vam-result (Sprint 6 Bloque 5)
 *
 * El atleta reporta el resultado de un TEST-VAM completado. El backend
 * actualiza el AthleteProfile con la VAM calculada + hr_max y marca la
 * sesión como completed.
 *
 * Errores típicos (ApiError con code):
 * - `not_a_vam_test` (la sesión no es session_type='test_vam').
 * - `vam_test_already_completed` (sesión ya completada).
 * - `distance_meters_invalid`, `hr_avg_invalid`, `hr_max_invalid`.
 * - 404 `session_not_found` (no le pertenece al user).
 */
export async function submitVamResult(
  sessionId: number,
  payload: VamResultPayload,
): Promise<VamResultResponse> {
  return apiFetch<VamResultResponse>(
    `/training-plans/active/sessions/${sessionId}/vam-result`,
    {
      method: "POST",
      body: payload,
    },
  );
}


// ---------------------------------------------------------------------
// Exportación FIT a reloj — F3 (sesión 51, v16.5/v16.6)
// ---------------------------------------------------------------------

/** Resultado de la descarga: blob del .fit + nombre de archivo del
 *  Content-Disposition (fallback genérico si el header no llega). */
export interface FitExportResult {
  blob: Blob;
  filename: string;
}

/**
 * Descarga el workout .fit de una sesión pendiente.
 *
 * ⚠️ Fetch DIRECTO con Authorization header (JWT de tokenStorage) — un
 * `<a href>` no lleva auth y el endpoint la exige. La respuesta es
 * binaria (octet-stream), por eso no pasa por `apiFetch` (JSON-only).
 *
 * Errores (ApiError.code): los reason de los gates del backend —
 * `fit_export_amateur_only` | `fit_export_vam_required` |
 * `fit_export_watch_required` | `fit_export_session_not_pending` |
 * `fit_export_type_not_exportable` | `session_not_found`.
 */
export async function exportSessionFit(
  sessionId: number,
): Promise<FitExportResult> {
  const { API_URL, tokenStorage } = await import("./client");
  const access = tokenStorage.getAccess();
  const resp = await fetch(
    `${API_URL}/training-plans/active/sessions/${sessionId}/export.fit`,
    {
      headers: access ? { Authorization: `Bearer ${access}` } : {},
    },
  );
  if (!resp.ok) {
    let code = "fit_export_failed";
    try {
      const body = (await resp.json()) as { error?: string };
      if (body.error) code = body.error;
    } catch {
      // binario/HTML inesperado — mantener código genérico
    }
    throw new Error(code);
  }
  const blob = await resp.blob();
  const disposition = resp.headers.get("Content-Disposition") ?? "";
  const match = /filename="([^"]+)"/.exec(disposition);
  return {
    blob,
    filename: match?.[1] ?? `nexum_${sessionId}.fit`,
  };
}
